import React, { createContext, useState, useEffect } from "react";

export const CartContext = createContext();

// 🔥 Add this hook so ProductDetail.js can import useCart
export const useCart = () => {
  const ctx = React.useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used within CartProvider");
  }
  return ctx;
};

export const CartProvider = ({ children }) => {
  const CART_KEY = "appCart";

  const [cart, setCart] = useState([]);

  // Load cart from storage
  useEffect(() => {
    const saved = localStorage.getItem(CART_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Ensure all prices are numeric
        const cleaned = parsed.map(item => ({
          ...item,
          price: Number(String(item.price).replace(/,/g, "")) || 0,
        }));
        setCart(cleaned);
      } catch {
        setCart([]);
      }
    }
  }, []);

  // Save cart
  const saveCart = (data) => {
    setCart(data);
    localStorage.setItem(CART_KEY, JSON.stringify(data));
  };

  // 🧹 Clean incoming item to force numeric price
  const cleanItem = (item) => ({
    ...item,
    qty: item.qty ?? 1,
    price: Number(String(item.price).replace(/,/g, "")) || 0,
  });

  // ADD TO CART
  const addToCart = (rawItem) => {
    const item = cleanItem(rawItem);

    const exists = cart.find((x) => x.id === item.id);

    if (exists) {
      const updated = cart.map((x) =>
        x.id === item.id ? { ...x, qty: x.qty + 1 } : x
      );
      return saveCart(updated);
    }

    saveCart([...cart, item]);
  };

  // UPDATE QTY
  const updateQty = (id, qty) => {
    const updated = cart.map((x) =>
      x.id === id ? { ...x, qty: Math.max(1, qty) } : x
    );
    saveCart(updated);
  };

  // REMOVE
  const removeItem = (id) => {
    saveCart(cart.filter((x) => x.id !== id));
  };

  // CLEAR CART
  const clearCart = () => saveCart([]);

  // TOTAL COUNT
  const cartCount = cart.reduce((sum, item) => sum + item.qty, 0);

  // TOTAL PRICE
  const cartTotal = cart.reduce((sum, item) => {
    const numericPrice = Number(String(item.price).replace(/,/g, "")) || 0;
    return sum + numericPrice * item.qty;
  }, 0);

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        updateQty,
        removeItem,
        clearCart,
        cartCount,
        cartTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
